import { FC, useMemo } from "react";
import { FloatingPagerItem, FloatingPagerWrapper, Title } from "./styles";
import { ReactComponent as NextSvg } from './assets/next.svg';
import { ReactComponent as PrevSvg } from './assets/prev.svg';
import { Position } from "../ChaptersNavigation";
import { FlexBox, useBreakpoint } from "@brg/ui";
import { useTranslation } from "react-i18next";
import { LayoutConst } from "core";
import { RectangularButtonHollow } from "shared/components/buttons";

const MAX_CHAPTERS_FOR_WIDE = 6;
const MAX_CHAPTERS_FOR_LINE = 5;
const MIDDLE_CHAPTER_IDX = 1;

const ArrowRight: FC = () => (
    <svg width="8" height="15" viewBox="0 0 8 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.15801 7.1333L0.223468 12.8735C-0.0744926 13.1617 -0.0744885 13.629 0.223476 13.9172C0.52144 14.2054 1.00453 14.2053 1.30249 13.9171L7.77653 7.65513C8.07449 7.36693 8.07449 6.89967 7.77653 6.61147L1.30249 0.349454C1.00453 0.0612519 0.52144 0.0612493 0.223476 0.349448C-0.0744885 0.637647 -0.0744926 1.10491 0.223468 1.39312L6.15801 7.1333Z" fill="#151514"/>
    </svg>
)

enum Direction {
    Next = "NEXT",
    Prev = "PREV"
};

const near = (items: string[], item: string, direction: Direction): string | undefined => {
    const index = items.indexOf(item);

    if (index === -1) {
        return undefined;
    }

    return items[direction === Direction.Next ? (index + 1) : (index - 1)];
};

interface FloatingPagerProps {
    currentIdentifier: string;
    identifiers: string[];
    onClick: (identifier: string) => void;
    position: Position;
};

export const FloatingPager: FC<FloatingPagerProps> = ({
    currentIdentifier,
    identifiers,
    position,
    onClick
}) => {
    const { t } = useTranslation();
    const isMobile = useBreakpoint(LayoutConst.screen.s -1);

    const showPages: string[] = useMemo(() => {
        const currentIndex = identifiers.indexOf(currentIdentifier);

        if (identifiers?.length <= MAX_CHAPTERS_FOR_WIDE) {
            return identifiers;
        }

        if (currentIndex === -1) {
            return [];
        }

        let needPrev: boolean = true;
        let needNext: boolean = true;
        let direction: Direction = Direction.Prev;
        let result: string[] = [currentIdentifier];

        while ((needPrev || needNext) && result.length < 5) {
            if (direction === Direction.Prev) {
                const item = near(identifiers, result[0], direction);
                if (!item) {
                    needPrev = false;
                    direction = Direction.Next;
                } else {
                    result.unshift(item);
                    if (needNext) {
                        direction = Direction.Next;
                    }
                }
            } else {
                const item = near(identifiers, result[result.length - 1], direction);
                if (!item) {
                    needNext = false;
                    direction = Direction.Prev;
                } else {
                    result.push(item);
                    if (needPrev) {
                        direction = Direction.Prev;
                    }
                }
            }
        }

        return result;
    }, [identifiers, currentIdentifier]);

    const prev: string = useMemo((): any => {
        return near(identifiers, currentIdentifier, Direction.Prev);
    }, [currentIdentifier, identifiers]);

    const next: string = useMemo((): any => {
        return near(identifiers, currentIdentifier, Direction.Next);
    }, [currentIdentifier, identifiers]);

    const showPrev = useMemo(() => {
        const currentIndex = showPages.indexOf(currentIdentifier);
        
        if (identifiers?.length <= MAX_CHAPTERS_FOR_LINE) {
            return false;
        }
        return currentIndex >= MIDDLE_CHAPTER_IDX
    } , [currentIdentifier, showPages, identifiers])

    const showNext = useMemo(() => {
        return identifiers?.length > MAX_CHAPTERS_FOR_WIDE && next
    } , [identifiers, next])


    const showNextButton = useMemo(
        () => 
        position === Position.bottom && 
        currentIdentifier !== identifiers[identifiers?.length - 1]
    , [identifiers, currentIdentifier, position])

    const showChapterName = true;

    if (showPages.length === 0) {
        return null;
    }

    return (
        <FloatingPagerWrapper position={position}>
          {!isMobile && (
            <>
            {showChapterName && (
                <Title>{t('card_constructor.project_edit.chapter_pages.title.name')}</Title>
            )}
            {showPrev && (
                <FloatingPagerItem
                    position={position} 
                    key={'floating_pager_prev'}
                    onClick={() => {
                        if (prev) {
                            onClick(prev);
                        }
                    }}
                    className={!prev ? 'disabled' : ''}
                    >
                <PrevSvg />
                </FloatingPagerItem>
            )}
                
            {showPages.map((value) => {
                const index = identifiers.indexOf(value);
                const isCurrent = value === currentIdentifier;

                return (
                    <FloatingPagerItem
                        position={position}
                        key={'floating_pager_' + value}
                        onClick={() => {
                            if (!isCurrent) {
                                onClick(value);
                            }
                        }}
                        className={isCurrent ? 'active' : ''}
                        >
                        {index + 1}
                    </FloatingPagerItem>
                );
            })}
            {showNext && (
                <FloatingPagerItem
                    position={position}
                    key={'floating_pager_next'}
                    onClick={() => {
                        if (next) {
                            onClick(next);
                        }
                    }}
                    className={showNext ? '' : 'disabled'}
                    >
                        <NextSvg />
                </FloatingPagerItem>
            )}
            
            </>
          )}

            {isMobile && (
                <FlexBox direction={'row'} directionMobile="row" alignMobile="center">
                {showChapterName && (
                    <Title>{t('card_constructor.project_edit.chapter_pages.title.name')}</Title>
                )}
                {showPrev && (
                    <FloatingPagerItem
                        position={position} 
                        key={'floating_pager_prev'}
                        onClick={() => {
                            if (prev) {
                                onClick(prev);
                            }
                        }}
                        className={!prev ? 'disabled' : ''}
                        >
                    <PrevSvg />
                    </FloatingPagerItem>
                )}
                {showPages.map((value) => {
                    const index = identifiers.indexOf(value);
                    const isCurrent = value === currentIdentifier;

                    return (
                        <FloatingPagerItem
                            position={position}
                            key={'floating_pager_' + value}
                            onClick={() => {
                                if (!isCurrent) {
                                    onClick(value);
                                }
                            }}
                            className={isCurrent ? 'active' : ''}
                            >
                            {index + 1}
                        </FloatingPagerItem>
                    );
                })}
                {showNext && (
                    <FloatingPagerItem
                        position={position}
                        key={'floating_pager_next'}
                        onClick={() => {
                            if (next) {
                                onClick(next);
                            }
                        }}
                        className={showNext ? '' : 'disabled'}
                        >
                            <NextSvg />
                    </FloatingPagerItem>
                )}
                </FlexBox>
            )}
                
            {showNextButton && (
                <RectangularButtonHollow onClick={() => onClick(next)}>
                    {t('card_constructor.project_edit.chapter_pages.buttons.next_chapter')}
                    <ArrowRight />
                </RectangularButtonHollow>
            )}
        </FloatingPagerWrapper>
    );
};import { FC, useMemo } from "react";
import { FloatingPagerItem, FloatingPagerWrapper, Title } from "./styles";
import { ReactComponent as NextSvg } from './assets/next.svg';
import { ReactComponent as PrevSvg } from './assets/prev.svg';
import { Position } from "../ChaptersNavigation";
import { FlexBox, useBreakpoint } from "@brg/ui";
import { useTranslation } from "react-i18next";
import { LayoutConst } from "core";
import { RectangularButtonHollow } from "shared/components/buttons";

const MAX_CHAPTERS_FOR_WIDE = 6;
const MAX_CHAPTERS_FOR_LINE = 5;
const MIDDLE_CHAPTER_IDX = 1;

const ArrowRight: FC = () => (
    <svg width="8" height="15" viewBox="0 0 8 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.15801 7.1333L0.223468 12.8735C-0.0744926 13.1617 -0.0744885 13.629 0.223476 13.9172C0.52144 14.2054 1.00453 14.2053 1.30249 13.9171L7.77653 7.65513C8.07449 7.36693 8.07449 6.89967 7.77653 6.61147L1.30249 0.349454C1.00453 0.0612519 0.52144 0.0612493 0.223476 0.349448C-0.0744885 0.637647 -0.0744926 1.10491 0.223468 1.39312L6.15801 7.1333Z" fill="#151514"/>
    </svg>
)

enum Direction {
    Next = "NEXT",
    Prev = "PREV"
};

const near = (items: string[], item: string, direction: Direction): string | undefined => {
    const index = items.indexOf(item);

    if (index === -1) {
        return undefined;
    }

    return items[direction === Direction.Next ? (index + 1) : (index - 1)];
};

interface FloatingPagerProps {
    currentIdentifier: string;
    identifiers: string[];
    onClick: (identifier: string) => void;
    position: Position;
};

export const FloatingPager: FC<FloatingPagerProps> = ({
    currentIdentifier,
    identifiers,
    position,
    onClick
}) => {
    const { t } = useTranslation();
    const isMobile = useBreakpoint(LayoutConst.screen.s -1);

    const showPages: string[] = useMemo(() => {
        const currentIndex = identifiers.indexOf(currentIdentifier);

        if (identifiers?.length <= MAX_CHAPTERS_FOR_WIDE) {
            return identifiers;
        }

        if (currentIndex === -1) {
            return [];
        }

        let needPrev: boolean = true;
        let needNext: boolean = true;
        let direction: Direction = Direction.Prev;
        let result: string[] = [currentIdentifier];

        while ((needPrev || needNext) && result.length < 5) {
            if (direction === Direction.Prev) {
                const item = near(identifiers, result[0], direction);
                if (!item) {
                    needPrev = false;
                    direction = Direction.Next;
                } else {
                    result.unshift(item);
                    if (needNext) {
                        direction = Direction.Next;
                    }
                }
            } else {
                const item = near(identifiers, result[result.length - 1], direction);
                if (!item) {
                    needNext = false;
                    direction = Direction.Prev;
                } else {
                    result.push(item);
                    if (needPrev) {
                        direction = Direction.Prev;
                    }
                }
            }
        }

        return result;
    }, [identifiers, currentIdentifier]);

    const prev: string = useMemo((): any => {
        return near(identifiers, currentIdentifier, Direction.Prev);
    }, [currentIdentifier, identifiers]);

    const next: string = useMemo((): any => {
        return near(identifiers, currentIdentifier, Direction.Next);
    }, [currentIdentifier, identifiers]);

    const showPrev = useMemo(() => {
        const currentIndex = showPages.indexOf(currentIdentifier);
        
        if (identifiers?.length <= MAX_CHAPTERS_FOR_LINE) {
            return false;
        }
        return currentIndex >= MIDDLE_CHAPTER_IDX
    } , [currentIdentifier, showPages, identifiers])

    const showNext = useMemo(() => {
        return identifiers?.length > MAX_CHAPTERS_FOR_WIDE && next
    } , [identifiers, next])


    const showNextButton = useMemo(
        () => 
        position === Position.bottom && 
        currentIdentifier !== identifiers[identifiers?.length - 1]
    , [identifiers, currentIdentifier, position])

    const showChapterName = true;

    if (showPages.length === 0) {
        return null;
    }

    return (
        <FloatingPagerWrapper position={position}>
          {!isMobile && (
            <>
            {showChapterName && (
                <Title>{t('card_constructor.project_edit.chapter_pages.title.name')}</Title>
            )}
            {showPrev && (
                <FloatingPagerItem
                    position={position} 
                    key={'floating_pager_prev'}
                    onClick={() => {
                        if (prev) {
                            onClick(prev);
                        }
                    }}
                    className={!prev ? 'disabled' : ''}
                    >
                <PrevSvg />
                </FloatingPagerItem>
            )}
                
            {showPages.map((value) => {
                const index = identifiers.indexOf(value);
                const isCurrent = value === currentIdentifier;

                return (
                    <FloatingPagerItem
                        position={position}
                        key={'floating_pager_' + value}
                        onClick={() => {
                            if (!isCurrent) {
                                onClick(value);
                            }
                        }}
                        className={isCurrent ? 'active' : ''}
                        >
                        {index + 1}
                    </FloatingPagerItem>
                );
            })}
            {showNext && (
                <FloatingPagerItem
                    position={position}
                    key={'floating_pager_next'}
                    onClick={() => {
                        if (next) {
                            onClick(next);
                        }
                    }}
                    className={showNext ? '' : 'disabled'}
                    >
                        <NextSvg />
                </FloatingPagerItem>
            )}
            
            </>
          )}

            {isMobile && (
                <FlexBox direction={'row'} directionMobile="row" alignMobile="center">
                {showChapterName && (
                    <Title>{t('card_constructor.project_edit.chapter_pages.title.name')}</Title>
                )}
                {showPrev && (
                    <FloatingPagerItem
                        position={position} 
                        key={'floating_pager_prev'}
                        onClick={() => {
                            if (prev) {
                                onClick(prev);
                            }
                        }}
                        className={!prev ? 'disabled' : ''}
                        >
                    <PrevSvg />
                    </FloatingPagerItem>
                )}
                {showPages.map((value) => {
                    const index = identifiers.indexOf(value);
                    const isCurrent = value === currentIdentifier;

                    return (
                        <FloatingPagerItem
                            position={position}
                            key={'floating_pager_' + value}
                            onClick={() => {
                                if (!isCurrent) {
                                    onClick(value);
                                }
                            }}
                            className={isCurrent ? 'active' : ''}
                            >
                            {index + 1}
                        </FloatingPagerItem>
                    );
                })}
                {showNext && (
                    <FloatingPagerItem
                        position={position}
                        key={'floating_pager_next'}
                        onClick={() => {
                            if (next) {
                                onClick(next);
                            }
                        }}
                        className={showNext ? '' : 'disabled'}
                        >
                            <NextSvg />
                    </FloatingPagerItem>
                )}
                </FlexBox>
            )}
                
            {showNextButton && (
                <RectangularButtonHollow onClick={() => onClick(next)}>
                    {t('card_constructor.project_edit.chapter_pages.buttons.next_chapter')}
                    <ArrowRight />
                </RectangularButtonHollow>
            )}
        </FloatingPagerWrapper>
    );
};